import { css, cssSafeValue } from "@assets/style/util/cssUtils"
import mergeClasses from "@assets/style/util/mergeClasses"
import { DiscoSpinner, DiscoTooltip } from "@disco-ui"
import DiscoLink, { DiscoLinkProps } from "@disco-ui/link/DiscoLink"
import { IconButton, IconButtonProps } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import { forwardRef, ReactNode } from "react"
import classes from "./DiscoIconButton.module.css"

export interface DiscoIconButtonProps
  extends Omit<IconButtonProps, "color">,
    TestIDProps {
  children?: ReactNode
  width?: React.CSSProperties["width"]
  height?: React.CSSProperties["height"]
  color?: React.CSSProperties["color"]
  background?: React.CSSProperties["background"]
  hoverColor?: React.CSSProperties["color"]
  hoverBackgroundColor?: React.CSSProperties["color"]
  svgStyles?: {
    width?: React.CSSProperties["width"]
    height?: React.CSSProperties["height"]
  }
  to?: DiscoLinkProps["to"]
  variant?: DiscoIconButtonVariant
  shouldDisplaySpinner?: boolean
  tooltip?: string
  tooltipPlacement?: "top" | "bottom" | "left" | "right"
  borderStyle?: React.CSSProperties["borderStyle"]
  linkProps?: Omit<DiscoLinkProps, "to" | "className">
  haveDisabledStyles?: boolean
}

type DiscoIconButtonVariant = "default" | "outlined" | "primary"

const DiscoIconButton = forwardRef<HTMLButtonElement, DiscoIconButtonProps>(
  (props, ref) => {
    const {
      children,
      width,
      height,
      testid,
      color,
      hoverColor,
      background,
      hoverBackgroundColor,
      classes: customClasses,
      className: customClassName,
      style: customStyle,
      svgStyles,
      to,
      variant = "default",
      shouldDisplaySpinner,
      tooltip,
      tooltipPlacement,
      borderStyle,
      linkProps,
      haveDisabledStyles,
      ...rest
    } = props

    if (shouldDisplaySpinner) {
      return <DiscoSpinner size={"sm"} fullWidth={false} />
    }
    let content: React.ReactNode

    const cssVariables = css({
      ...customStyle,
      "--icon-button-width": cssSafeValue(width),
      "--icon-button-height": cssSafeValue(height),
      "--icon-button-background": background,
      "--icon-button-svg-width": cssSafeValue(svgStyles?.width),
      "--icon-button-svg-height": cssSafeValue(svgStyles?.height),
      "--icon-border-style": borderStyle,
      "--icon-button-hover-color": hoverColor,
      "--icon-button-hover-background-color": hoverBackgroundColor,
      "--icon-button-color": color,
    })

    if (to) {
      content = (
        <DiscoLink
          {...linkProps}
          to={to}
          className={classNames(classes.root, customClasses?.root, customClasses?.label)}
          data-testid={testid}
          data-variant={variant}
          data-have-disabled-styles={haveDisabledStyles}
          style={cssVariables}
        >
          {children}
        </DiscoLink>
      )
    } else {
      content = (
        <IconButton
          ref={ref}
          className={classNames(
            {
              [classes.noPadding]: Boolean(width || height),
            },
            customClassName
          )}
          classes={mergeClasses(classes, customClasses)}
          data-testid={testid}
          data-variant={variant}
          data-have-disabled-styles={haveDisabledStyles}
          style={cssVariables}
          {...rest}
        >
          {children}
        </IconButton>
      )
    }

    if (tooltip) {
      return (
        <DiscoTooltip content={tooltip} placement={tooltipPlacement}>
          <div>{content}</div>
        </DiscoTooltip>
      )
    }

    return <>{content}</>
  }
)

export default DiscoIconButton

interface SkeletonProps {
  className?: string
  height?: number
  width?: number
}

export function DiscoIconButtonSkeleton(props: SkeletonProps) {
  const { height, width, className } = props

  return (
    <Skeleton
      variant={"rect"}
      className={className}
      style={{
        height: height ?? 48,
        width: width ?? 48,
        borderRadius: "var(--groovy-border-radius-8)",
      }}
    />
  )
}
